import {
  EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS,
  SOLANA_CHAIN_REFERENCE,
} from "@repo/caip";
import { z } from "zod";
import { lazySchema } from "@repo/lazy-schema";

export const WITHDRAWAL_BALANCES_MAX_ACCOUNTS = 16;
export const WITHDRAWAL_BALANCES_MAX_ASSETS = 256;
export const WITHDRAWAL_BALANCES_TTL_MS = 30_000;

const sortedUnique = (values: readonly string[]) =>
  values.every((value, index) => index === 0 || values[index - 1]! < value);

/**
 * The vaults a withdrawal reads: an EVM vault (lowercased canonical form) or
 * an Investing Account's Solana mainnet vault (its Swig wallet, base58
 * verbatim). Each identity below is checked against its own chain.
 */
const SOLANA_MAINNET_CHAIN_REF = `solana:${SOLANA_CHAIN_REFERENCE}`;
const BASE58_KEY = "[1-9A-HJ-NP-Za-km-z]{32,44}";
const isChainRef = (value: string): boolean =>
  value === SOLANA_MAINNET_CHAIN_REF ||
  (/^eip155:[1-9]\d*$/u.test(value) &&
    Number.isSafeInteger(Number(value.slice("eip155:".length))));
const accountChainRef = (accountRef: string): string =>
  accountRef.slice(0, accountRef.lastIndexOf(":"));
const isAccountRef = (value: string): boolean => {
  const chainRef = accountChainRef(value);
  if (!isChainRef(chainRef)) return false;
  const address = value.slice(chainRef.length + 1);
  return chainRef === SOLANA_MAINNET_CHAIN_REF
    ? new RegExp(`^${BASE58_KEY}$`, "u").test(address)
    : /^0x[0-9a-f]{40}$/u.test(address);
};
/** Canonical asset identity on `chainRef`: EVM ERC-20/native, Solana SPL/SOL. */
const isAssetOnChain = (assetId: string, chainRef: string): boolean => {
  if (!isChainRef(chainRef) || !assetId.startsWith(`${chainRef}/`))
    return false;
  const asset = assetId.slice(chainRef.length + 1);
  return chainRef === SOLANA_MAINNET_CHAIN_REF
    ? asset === "slip44:501" ||
        new RegExp(`^spl:${BASE58_KEY}$`, "u").test(asset)
    : asset === "slip44:60" ||
        (/^erc20:0x[0-9a-f]{40}$/u.test(asset) &&
          asset !== `erc20:${EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS}`);
};
const accountRef = lazySchema(() => z.string().refine(isAccountRef));
const accountRefs = lazySchema(() =>
  z
    .array(accountRef)
    .min(1)
    .max(WITHDRAWAL_BALANCES_MAX_ACCOUNTS)
    .refine(sortedUnique, {
      message: "Accounts must be canonical, sorted, and unique",
    }),
);
const scope = lazySchema(() => ({
  portfolioId: z.string().trim().min(1).max(256),
  accountRefs,
}));

/**
 * The caller selects the exact portfolio account set (its EVM vaults and its
 * Solana vault), never amounts.
 *
 * Without `assetIds` the engine discovers candidates from indexed inventory
 * (an expensive aggregate). With `assetIds` — the candidate set of an earlier
 * full read of the same scope — it skips discovery and rechecks eligibility.
 * Retained assets are read live; filtered identities are explicitly reported.
 * Each asset must be on one of the requested accounts' chains.
 */
export const WithdrawalBalancesInputSchema = lazySchema(() =>
  z
    .object({
      ...scope,
      assetIds: z
        .array(z.string())
        .min(1)
        .max(WITHDRAWAL_BALANCES_MAX_ASSETS)
        .refine(sortedUnique, {
          message: "Assets must be sorted and unique",
        })
        .optional(),
    })
    .strict()
    .refine(
      (value) =>
        (value.assetIds ?? []).every((assetId) =>
          value.accountRefs.some((accountRef) =>
            isAssetOnChain(assetId, accountChainRef(accountRef)),
          ),
        ),
      {
        message:
          "Each asset must be canonical and on a requested account's chain",
      },
    ),
);
export type WithdrawalBalancesInput = z.infer<
  typeof WithdrawalBalancesInputSchema
>;

const rowIdentity = lazySchema(() => ({
  accountRef,
  chainRef: z.string().refine(isChainRef),
  assetId: z.string(),
}));
const RAW_AMOUNT = /^(?:0|[1-9]\d*)$/u;
const rawAmount = lazySchema(() => z.string().regex(RAW_AMOUNT));
const identityValid = (row: {
  accountRef: string;
  assetId: string;
  chainRef: string;
}) =>
  accountChainRef(row.accountRef) === row.chainRef &&
  isAssetOnChain(row.assetId, row.chainRef);
const balance = lazySchema(() =>
  z
    .object({
      ...rowIdentity,
      decimals: z.number().int().min(0).max(255),
      symbol: z.string().trim().min(1),
      priceUsd: z.number().positive().optional(),
      onchainRaw: rawAmount,
      heldRaw: rawAmount,
      spendableRaw: rawAmount,
      /** The pinned height the balance was read at: a block on EVM, a slot on Solana. */
      blockNumber: rawAmount,
    })
    .strict()
    .refine(identityValid, {
      message: "Balance identities must share one canonical chain",
    }),
);
const unavailableAsset = lazySchema(() =>
  z
    .object({
      ...rowIdentity,
      blockNumber: rawAmount.optional(),
      reason: z.enum([
        "metadata_unavailable",
        "balance_unavailable",
        "source_debit_block_unproven",
        "pinned_block_before_source_debit",
      ]),
    })
    .strict()
    .refine(identityValid, {
      message: "Unavailable asset identities must share one canonical chain",
    }),
);

const excludedAsset = lazySchema(() =>
  z
    .object({
      ...rowIdentity,
      reason: z.enum([
        "asset_manually_blocklisted",
        "asset_unpriced_unadmitted",
        "asset_unverified",
      ]),
    })
    .strict()
    .refine(identityValid, {
      message: "Excluded asset identities must share one canonical chain",
    }),
);

/** Advisory live balances. Indexed discovery does not certify exhaustive chain inventory. */
export const WithdrawalBalancesSchema = lazySchema(() =>
  z
    .object({
      ...scope,
      capturedAt: z.iso.datetime(),
      expiresAt: z.iso.datetime(),
      /** `skipped`: a refresh of caller-supplied `assetIds`; discovery did not run. */
      inventoryStatus: z.enum(["indexed", "unavailable", "skipped"]),
      indexedAsOf: z.iso.datetime().nullable(),
      /**
       * Discovered candidates beyond the 256-row read bound (typically spam
       * airdrops). Their balances were not read; nothing about them is implied.
       */
      omittedAssetCount: z.number().int().nonnegative(),
      balances: z.array(balance).max(WITHDRAWAL_BALANCES_MAX_ASSETS),
      unavailableAssets: z
        .array(unavailableAsset)
        .max(WITHDRAWAL_BALANCES_MAX_ASSETS),
      /** Intentional exclusions from requested refresh candidates; never amounts. */
      excludedAssets: z
        .array(excludedAsset)
        .max(WITHDRAWAL_BALANCES_MAX_ASSETS)
        .optional(),
    })
    .strict()
    .refine(
      (value) =>
        Date.parse(value.expiresAt) ===
        Date.parse(value.capturedAt) + WITHDRAWAL_BALANCES_TTL_MS,
      { message: "Withdrawal balance observations expire after 30 seconds" },
    )
    .refine(
      (value) =>
        (value.inventoryStatus === "indexed") === (value.indexedAsOf !== null),
      { message: "Indexed inventory requires an accounting timestamp" },
    )
    .refine(
      (value) =>
        value.inventoryStatus !== "skipped" || value.omittedAssetCount === 0,
      { message: "A refresh accounts for every requested asset" },
    )
    .refine(
      (value) => {
        const rows = [
          ...value.balances,
          ...value.unavailableAssets,
          ...(value.excludedAssets ?? []),
        ];
        return (
          rows.length <= WITHDRAWAL_BALANCES_MAX_ASSETS &&
          rows.every((row) => value.accountRefs.includes(row.accountRef)) &&
          new Set(rows.map((row) => `${row.accountRef}/${row.assetId}`))
            .size === rows.length
        );
      },
      {
        message:
          "Each candidate must belong to one requested account and occur once",
      },
    )
    .refine(
      (value) =>
        value.balances.every((row) => {
          // Refinements run past a failed field: never convert a malformed amount.
          if (
            ![row.onchainRaw, row.heldRaw, row.spendableRaw].every((amount) =>
              RAW_AMOUNT.test(amount),
            )
          )
            return false;
          const onchain = BigInt(row.onchainRaw);
          const held = BigInt(row.heldRaw);
          return (
            BigInt(row.spendableRaw) === (onchain > held ? onchain - held : 0n)
          );
        }),
      { message: "Spendable balances must equal the raw balance after holds" },
    ),
);

export type WithdrawalBalances = z.infer<typeof WithdrawalBalancesSchema>;
